import moment from "moment";

export function formatarValorEmRealBR(value: any) {
  const numericValue =
    typeof value === "string"
      ? parseFloat(value.replace(",", "."))
      : Number(value);

  if (isNaN(numericValue)) {
    return 0;
  }

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(numericValue);
}

export function formatarNumeroPadrao(value: any) {
  if (typeof value === "number") {
    if (value % 1 === 0) {
      return value.toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    } else {
      return value.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
  }

  return value;
}

const getCurrentMonth = () => {
  return new Date().getMonth() + 1;
};

export const meses = (t: any) => [
  { label: t("utils.months.quarter_01"), value: [1, 2, 3] },
  { label: t("utils.months.quarter_02"), value: [4, 5, 6] },
  { label: t("utils.months.quarter_03"), value: [7, 8, 9] },
  { label: t("utils.months.quarter_04"), value: [10, 11, 12] },
  { label: t("utils.months.current_month"), value: getCurrentMonth() },
  { label: t("utils.months.next_month"), value: getCurrentMonth() + 1 },
  { label: t("utils.months.january"), value: 1 },
  { label: t("utils.months.february"), value: 2 },
  { label: t("utils.months.march"), value: 3 },
  { label: t("utils.months.april"), value: 4 },
  { label: t("utils.months.may"), value: 5 },
  { label: t("utils.months.june"), value: 6 },
  { label: t("utils.months.july"), value: 7 },
  { label: t("utils.months.august"), value: 8 },
  { label: t("utils.months.september"), value: 9 },
  { label: t("utils.months.october"), value: 10 },
  { label: t("utils.months.november"), value: 11 },
  { label: t("utils.months.december"), value: 12 },
  { label: t("utils.months.first_semester"), value: [1, 2, 3, 4, 5, 6] },
  { label: t("utils.months.second_semester"), value: [7, 8, 9, 10, 11, 12] },
  {
    label: t("utils.months.annual"),
    value: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  },
];

export const mesesPipeline = (t: any) => [
  {
    label: t("utils.months.january"),
    value: 1,
  },
  {
    label: t("utils.months.february"),
    value: 2,
  },
  {
    label: t("utils.months.march"),
    value: 3,
  },
  {
    label: t("utils.months.april"),
    value: 4,
  },
  {
    label: t("utils.months.may"),
    value: 5,
  },
  {
    label: t("utils.months.june"),
    value: 6,
  },
  {
    label: t("utils.months.july"),
    value: 7,
  },
  {
    label: t("utils.months.august"),
    value: 8,
  },
  {
    label: t("utils.months.september"),
    value: 9,
  },
  {
    label: t("utils.months.october"),
    value: 10,
  },
  {
    label: t("utils.months.november"),
    value: 11,
  },
  {
    label: t("utils.months.december"),
    value: 12,
  },
];

export function obterNomeDoMes(mes: any, t: any) {
  const mesEncontrado = mesesPipeline(t).find((m) => m.value === mes);
  return mesEncontrado ? mesEncontrado.label : t("utils.months.invalid_month");
}

export const renderMessageContent = (content: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = content.split(urlRegex);

  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

const OVERVIEW_LOCAL_DATE_FORMATS = [
  "YYYY-MM-DD HH:mm:ss.SSS",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DDTHH:mm:ss.SSS",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm",
  "YYYY-MM-DD",
  "DD/MM/YYYY HH:mm:ss",
  "DD/MM/YYYY HH:mm",
  "DD/MM/YYYY",
  "DD/MM/YY HH:mm",
  "DD/MM/YY",
];

const TIMEZONE_SUFFIX_REGEX = /(Z|[+-]\d{2}:?\d{2})$/i;
const ISO_DATE_PREFIX_REGEX = /^(\d{4})-(\d{2})-(\d{2})/;
const TIME_PART_REGEX = /(?:T|\s)(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?/;

export type OverviewDateTimeOptions = {
  preserveApiTime?: boolean;
  dateFormat?: string;
  dateTimeFormat?: string;
};

const OVERVIEW_TRACKED_STATUS_IDS = new Set<number>([
  21,
  22,
  37,
  38,
  39,
  40,
  41,
  71,
  72,
  73,
  87,
  11,
  12,
  13,
  1,
  2,
  18,
  23,
  24,
  25,
]);

function hasExplicitTimezone(value: string) {
  return TIMEZONE_SUFFIX_REGEX.test(value.trim());
}

function hasMeaningfulTime(value: any) {
  if (value instanceof Date) {
    return (
      value.getHours() !== 0 ||
      value.getMinutes() !== 0 ||
      value.getSeconds() !== 0 ||
      value.getMilliseconds() !== 0
    );
  }

  if (typeof value !== "string") return true;

  const timeParts = value.match(TIME_PART_REGEX);
  if (!timeParts) return false;

  const [, hours = "00", minutes = "00", seconds = "00"] = timeParts;
  return Number(hours) !== 0 || Number(minutes) !== 0 || Number(seconds) !== 0;
}

export function parseOverviewDateTime(
  value: any,
  options: Pick<OverviewDateTimeOptions, "preserveApiTime"> = {},
) {
  return parseOverviewDateTimeWithOptions(value, options);
}

function parseOverviewDateTimeWithOptions(
  value: any,
  options: Pick<OverviewDateTimeOptions, "preserveApiTime"> = {},
) {
  if (!value) return null;

  if (value instanceof Date) {
    const date = moment(value);
    return date.isValid() ? date : null;
  }

  if (typeof value === "number") {
    const date = moment(value);
    return date.isValid() ? date : null;
  }

  if (typeof value !== "string") return null;

  const rawValue = value.trim();
  if (!rawValue) return null;

  const datePrefix = rawValue.match(ISO_DATE_PREFIX_REGEX);
  if (!hasMeaningfulTime(rawValue) && datePrefix) {
    const date = moment(
      `${datePrefix[1]}-${datePrefix[2]}-${datePrefix[3]}`,
      "YYYY-MM-DD",
      true,
    );
    return date.isValid() ? date : null;
  }

  if (options.preserveApiTime && hasExplicitTimezone(rawValue)) {
    const localWallClockValue = rawValue.replace(TIMEZONE_SUFFIX_REGEX, "");
    const date = moment(localWallClockValue, OVERVIEW_LOCAL_DATE_FORMATS, true);
    return date.isValid() ? date : null;
  }

  const date = hasExplicitTimezone(rawValue)
    ? moment(rawValue)
    : moment(rawValue, OVERVIEW_LOCAL_DATE_FORMATS, true);

  if (date.isValid()) return date;

  const fallbackDate = moment(rawValue);
  return fallbackDate.isValid() ? fallbackDate : null;
}

export function formatOverviewDateTime(
  value: any,
  dateFormatOrOptions: string | OverviewDateTimeOptions = "DD/MM/YY",
  dateTimeFormat = "DD/MM/YYYY HH:mm",
  options: OverviewDateTimeOptions = {},
) {
  const resolvedOptions =
    typeof dateFormatOrOptions === "object" ? dateFormatOrOptions : options;
  const dateFormat =
    typeof dateFormatOrOptions === "object"
      ? dateFormatOrOptions.dateFormat ?? "DD/MM/YY"
      : dateFormatOrOptions;
  const resolvedDateTimeFormat =
    typeof dateFormatOrOptions === "object"
      ? dateFormatOrOptions.dateTimeFormat ?? "DD/MM/YYYY HH:mm"
      : dateTimeFormat;

  const date = parseOverviewDateTimeWithOptions(value, resolvedOptions);
  if (!date) return "-";

  return hasMeaningfulTime(value)
    ? date.format(resolvedDateTimeFormat)
    : date.format(dateFormat);
}

export function isOverviewDateOverdue(
  value: any,
  options: Pick<OverviewDateTimeOptions, "preserveApiTime"> = {},
) {
  const deadline = getOverviewDeadline(value, options);
  return deadline !== null && deadline <= Date.now();
}

export function getOverviewDeadline(
  value: any,
  options: Pick<OverviewDateTimeOptions, "preserveApiTime"> = {},
) {
  const date = parseOverviewDateTimeWithOptions(value, options);
  if (!date) return null;
  return (hasMeaningfulTime(value) ? date : date.endOf("day")).valueOf();
}

export function isOverviewStatusTracked(statusId: unknown) {
  return OVERVIEW_TRACKED_STATUS_IDS.has(Number(statusId));
}

export function getTdClassByDate(
  dataValueEntrega: any,
  statusId: any,
  defaultClass = "text-[#1E1E1E]",
  options: Pick<OverviewDateTimeOptions, "preserveApiTime"> = {},
) {
  if (
    isOverviewStatusTracked(statusId) &&
    isOverviewDateOverdue(dataValueEntrega, options)
  ) {
    return "text-red-500";
  }

  return defaultClass;
}

export const capitaisBrasileiras = [
  "Nacional - Todo Brasil",
  "Brasília",
  "São Paulo",
  "Rio de Janeiro",
  "Salvador",
  "Fortaleza",
  "Belo Horizonte",
  "Manaus",
  "Curitiba",
  "Recife",
  "Porto Alegre",
  "Belém",
  "Goiânia",
  "Guarulhos",
  "Campinas",
  "São Luís",
  "Maceió",
  "Duque de Caxias",
  "Natal",
  "Teresina",
  "Campo Grande",
];

export async function prepareFormData(formData: FormData, dadosExtras: any) {
  if (!dadosExtras || typeof dadosExtras !== "object") {
    console.error("dadosExtras está vazio ou não é um objeto:", dadosExtras);
    return formData;
  }

  Object.keys(dadosExtras).forEach((key) => {
    const value = dadosExtras[key];

    if (value !== undefined && value !== null) {
      if (key === "files") {
        const arquivos = dadosExtras[key];
        for (let i = 0; i < arquivos.length; i++) {
          formData.append("files", arquivos[i]);
        }
      } else if (key === "proposta" || key === "links") {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, String(value));
      }
    } else {
      console.warn(`Valor ignorado para a chave ${key}:`, value);
    }
  });
  return formData;
}

export function safeJsonParse(jsonString: string): any[] {
  if (
    !jsonString ||
    jsonString === "Vazio" ||
    jsonString === "" ||
    jsonString === "null"
  ) {
    return [];
  }

  try {
    const parsed = JSON.parse(jsonString);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn("Erro ao fazer parse do JSON:", jsonString, error);
    return [];
  }
}
